import { Injectable, inject } from '@angular/core';
import { NavigationExtras, Router, UrlTree } from '@angular/router';

export const RETURN_CONTEXT_QUERY_PARAM = 'returnContext';

const RETURN_CONTEXT_VERSION = 1;
const RETURN_CONTEXT_STORAGE_KEY = 'cms.returnNavigation';
const MAX_RETURN_CONTEXT_DEPTH = 8;
const MAX_STORED_ROUTE_CONTEXTS = 256;
const MAX_RETURN_LABEL_LENGTH = 100;

export interface ReturnNavigationEntry {
  url: string;
  label: string;
}

interface LegacyReturnNavigationPayload {
  version: typeof RETURN_CONTEXT_VERSION;
  entries: ReturnNavigationEntry[];
}

interface StoredRouteContext {
  url: string;
  entries: ReturnNavigationEntry[];
}

interface ReturnNavigationStore {
  version: typeof RETURN_CONTEXT_VERSION;
  routes: StoredRouteContext[];
}

export interface ResolvedReturnNavigation {
  urlTree: UrlTree;
  label: string;
  contextual: boolean;
}

export type ReturnNavigationCommands = string | readonly (string | number)[];

@Injectable({ providedIn: 'root' })
export class ReturnNavigationService {
  private readonly router = inject(Router);
  private readonly memoryStore = new Map<string, ReturnNavigationEntry[]>();

  push(
    commands: readonly (string | number)[],
    label: string,
    extras: NavigationExtras = {},
  ): UrlTree {
    const target = this.createCleanUrlTree(commands, extras);
    const currentEntry = this.normaliseEntry({
      url: this.currentUrlWithoutContext(),
      label,
    });
    const existingEntries = this.readCurrentEntries();
    const entries = currentEntry
      ? this.normaliseStack([currentEntry, ...existingEntries])
      : existingEntries;

    this.storeEntries(target, entries);
    return target;
  }

  pushUrl(url: string, label: string): UrlTree {
    const target = this.safeUrlTree(url) ?? this.router.createUrlTree(['/']);
    this.removeLegacyContext(target);

    const currentEntry = this.normaliseEntry({
      url: this.currentUrlWithoutContext(),
      label,
    });
    const existingEntries = this.readCurrentEntries();
    const entries = currentEntry
      ? this.normaliseStack([currentEntry, ...existingEntries])
      : existingEntries;

    this.storeEntries(target, entries);
    return target;
  }

  preserve(
    commands: readonly (string | number)[],
    extras: NavigationExtras = {},
  ): UrlTree {
    const target = this.createCleanUrlTree(commands, extras);
    this.storeEntries(target, this.readCurrentEntries());
    return target;
  }

  preserveUrl(url: string): UrlTree {
    const target = this.safeUrlTree(url) ?? this.router.createUrlTree(['/']);
    this.removeLegacyContext(target);
    this.storeEntries(target, this.readCurrentEntries());
    return target;
  }

  resolve(
    fallbackCommands: ReturnNavigationCommands,
    fallbackLabel: string,
  ): ResolvedReturnNavigation {
    const [entry, ...remainingEntries] = this.readCurrentEntries();

    if (entry) {
      const target = this.safeUrlTree(entry.url);
      if (target) {
        this.removeLegacyContext(target);
        this.storeEntries(target, remainingEntries);
        return {
          urlTree: target,
          label: entry.label,
          contextual: true,
        };
      }
    }

    const fallback = this.fallbackUrlTree(fallbackCommands);
    this.storeEntries(fallback, []);
    return {
      urlTree: fallback,
      label: fallbackLabel,
      contextual: false,
    };
  }

  hasContext(): boolean {
    return this.readCurrentEntries().length > 0;
  }

  clear(): void {
    this.memoryStore.clear();

    try {
      globalThis.sessionStorage?.removeItem(RETURN_CONTEXT_STORAGE_KEY);
    } catch {
      // In-memory navigation remains available if browser storage is unavailable.
    }
  }

  private createCleanUrlTree(
    commands: readonly (string | number)[],
    extras: NavigationExtras,
  ) {
    const queryParams = { ...(extras.queryParams ?? {}) };
    delete queryParams[RETURN_CONTEXT_QUERY_PARAM];

    return this.router.createUrlTree([...commands], {
      ...extras,
      queryParams,
      queryParamsHandling: undefined,
    });
  }

  private currentUrlWithoutContext() {
    const current =
      this.safeUrlTree(typeof this.router.url === 'string' ? this.router.url : '/') ??
      this.router.createUrlTree(['/']);
    this.removeLegacyContext(current);
    return this.router.serializeUrl(current);
  }

  private readCurrentEntries() {
    const current = this.safeUrlTree(typeof this.router.url === 'string' ? this.router.url : '/');
    if (!current) {
      return [];
    }

    const legacyEncoded = current.queryParams[RETURN_CONTEXT_QUERY_PARAM];
    if (typeof legacyEncoded === 'string') {
      return this.decodeLegacyContext(legacyEncoded);
    }

    return this.readStoredEntries(this.cleanUrl(current));
  }

  private readStoredEntries(url: string) {
    const stored = this.readStore().find((context) => context.url === url)?.entries;
    return this.normaliseStack(stored ?? []);
  }

  private storeEntries(target: UrlTree, entries: ReturnNavigationEntry[]) {
    const url = this.cleanUrl(target);
    const normalisedEntries = this.normaliseStack(entries);
    const routes = this.readStore().filter((context) => context.url !== url);

    if (normalisedEntries.length > 0) {
      routes.unshift({ url, entries: normalisedEntries });
    }

    this.writeStore(routes.slice(0, MAX_STORED_ROUTE_CONTEXTS));
  }

  private readStore(): StoredRouteContext[] {
    try {
      const value = globalThis.sessionStorage?.getItem(RETURN_CONTEXT_STORAGE_KEY);
      if (value) {
        const store = JSON.parse(value) as Partial<ReturnNavigationStore>;
        if (store.version === RETURN_CONTEXT_VERSION && Array.isArray(store.routes)) {
          return this.normaliseStoredRoutes(store.routes);
        }
      }
    } catch {
      // Fall through to the in-memory copy.
    }

    return Array.from(this.memoryStore, ([url, entries]) => ({ url, entries }));
  }

  private writeStore(routes: StoredRouteContext[]) {
    this.memoryStore.clear();
    for (const context of routes) {
      this.memoryStore.set(context.url, context.entries);
    }

    const store: ReturnNavigationStore = {
      version: RETURN_CONTEXT_VERSION,
      routes,
    };

    try {
      globalThis.sessionStorage?.setItem(RETURN_CONTEXT_STORAGE_KEY, JSON.stringify(store));
    } catch {
      // The in-memory copy keeps same-page navigation working.
    }
  }

  private normaliseStoredRoutes(routes: StoredRouteContext[]) {
    const result: StoredRouteContext[] = [];

    for (const context of routes) {
      const url = typeof context?.url === 'string' ? context.url.trim() : '';
      const entries = Array.isArray(context?.entries)
        ? this.normaliseStack(context.entries)
        : [];

      if (!this.isSafeInternalUrl(url) || entries.length === 0) {
        continue;
      }

      result.push({ url, entries });
      if (result.length === MAX_STORED_ROUTE_CONTEXTS) {
        break;
      }
    }

    return result;
  }

  private decodeLegacyContext(value: string) {
    try {
      const payload = JSON.parse(value) as Partial<LegacyReturnNavigationPayload>;
      if (payload.version !== RETURN_CONTEXT_VERSION || !Array.isArray(payload.entries)) {
        return [];
      }

      return this.normaliseStack(payload.entries);
    } catch {
      return [];
    }
  }

  private normaliseStack(entries: ReturnNavigationEntry[]) {
    const result: ReturnNavigationEntry[] = [];

    for (const entry of entries) {
      const normalised = this.normaliseEntry(entry);
      if (!normalised || result.at(-1)?.url === normalised.url) {
        continue;
      }

      result.push(normalised);
      if (result.length === MAX_RETURN_CONTEXT_DEPTH) {
        break;
      }
    }

    return result;
  }

  private normaliseEntry(entry: Partial<ReturnNavigationEntry>) {
    const url = typeof entry.url === 'string' ? entry.url.trim() : '';
    const label =
      typeof entry.label === 'string'
        ? entry.label.trim().slice(0, MAX_RETURN_LABEL_LENGTH)
        : '';

    if (!label || !this.isSafeInternalUrl(url)) {
      return null;
    }

    return { url, label };
  }

  private fallbackUrlTree(commands: ReturnNavigationCommands) {
    if (typeof commands === 'string') {
      const target = this.safeUrlTree(commands) ?? this.router.createUrlTree(['/']);
      this.removeLegacyContext(target);
      return target;
    }

    return this.createCleanUrlTree(commands, {});
  }

  private cleanUrl(urlTree: UrlTree) {
    const clean = this.router.parseUrl(this.router.serializeUrl(urlTree));
    this.removeLegacyContext(clean);
    return this.router.serializeUrl(clean);
  }

  private removeLegacyContext(urlTree: UrlTree) {
    delete urlTree.queryParams[RETURN_CONTEXT_QUERY_PARAM];
  }

  private safeUrlTree(url: string) {
    if (!this.isSafeInternalUrl(url)) {
      return null;
    }

    try {
      return this.router.parseUrl(url);
    } catch {
      return null;
    }
  }

  private isSafeInternalUrl(url: unknown): url is string {
    return (
      typeof url === 'string' &&
      url.startsWith('/') &&
      !url.startsWith('//') &&
      !url.includes('\\')
    );
  }
}
